import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Plus, FileText, Filter, Eye, Edit2,
  Printer, CreditCard, RefreshCw, X, Truck, ExternalLink,
  Package, DollarSign, XCircle, TrendingUp,
} from 'lucide-react'
import { ExportButton } from '../../components/common/ExportButton'
import { ActionMenu, type ActionMenuItem } from '../../components/common/ActionMenu'
import { PageHeader } from '../../components/ui/PageHeader'
import { Pagination } from '../../components/ui/Pagination'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { useCurrency } from '../../hooks/useCurrency'
import { useAuthStore } from '../../store/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import { NotAuthorizedView } from '../../components/permissions/NotAuthorizedView'
import { useDebounce } from '../../hooks/useDebounce'
import { useUserLocationIds } from '../../hooks/useUserLocations'
import {
  fetchSalesOrdersEnhanced, fetchSalesOrdersActiveTotal, fetchSalesOrdersForExport,
  fetchPaymentAccounts, fetchOrderPayments, fetchWarehouses,
} from '../../lib/supabaseQueries'
import { methodLabel } from '../../lib/paymentReport/methodLabels'
import { displayDate } from '../../utils/dates'
import { supabase } from '../../lib/supabase'
import { fetchAllPaged } from '../../lib/pagination'
import { cn } from '../../utils/cn'
import toast from 'react-hot-toast'
import { cancelSaleWithReversal, resolveAccountsByMappingKeys } from '../../lib/accountingService'
import { useIdempotencyKey } from '../../hooks/useIdempotencyKey'
import { logAction } from '../../lib/auditLog'
import { CUSTOMER_CREDIT_MESSAGES, getRandomMessage, formatMessage } from '../../lib/advanceMessages'
import { ensureShareToken, publicInvoiceUrl } from '../../lib/shareToken'
import { getAllCustomerBalances } from '../../lib/contactBalances'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

const PAY_TERM_DAYS: Record<string, number> = {
  net_7: 7, net_15: 15, net_30: 30, net_45: 45, net_60: 60,
}

function getDueDate(createdAt: string, payTerm: string | null): Date | null {
  if (!payTerm || payTerm === 'none' || payTerm === 'cash_on_delivery') return null
  const key = payTerm.toLowerCase().replace(/[\s-]/g, '_')
  const days = PAY_TERM_DAYS[key]
  if (!days) return null
  const d = new Date(createdAt)
  d.setDate(d.getDate() + days)
  return d
}

function computePaymentStatus(
  total: number,
  amountPaid: number,
  createdAt: string,
  payTerm: string | null,
  storedStatus: string,
): string {
  if (storedStatus) return storedStatus
  if (amountPaid >= total) return 'paid'
  const due = getDueDate(createdAt, payTerm)
  const isOverdue = due ? new Date() > due : false
  if (amountPaid > 0) return isOverdue ? 'overdue' : 'partial'
  return isOverdue ? 'overdue' : 'due'
}

const PAYMENT_STATUS_BADGE: Record<string, string> = {
  paid: 'badge-green',
  partial: 'badge-blue',
  due: 'badge-orange',
  overdue: 'badge-red',
  unpaid: 'badge-red',
  credit: 'badge-blue',
  cancelled: 'badge-gray',
}

const STATUS_BADGE: Record<string, string> = {
  final: 'badge-green',
  draft: 'badge-gray',
  quotation: 'badge-blue',
  pro_forma: 'badge-orange',
  on_hold: 'badge-orange',
  cancelled: 'badge-red',
  refunded: 'badge-orange',
  returned: 'badge-orange',
}

const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'Card', 'Cheque', 'Mobile Money', 'Credit']

type SalesRow = {
  id: string; order_number: string; total: number; amount_paid: number
  payment_status: string; status: string; created_at: string
  payment_method: string | null
  payment_methods: string[]
  payment_indicated: boolean | null
  location_id: string | null
  share_token: string | null
  location: { id: string; name: string } | null
  customers: { id: string; name: string; business_name: string | null; phone: string | null; email: string | null } | null
  non_refund_returns_total: number
  meta: { opening_balance_bf?: boolean } | null
}

// Mode of Payment for the sales list — derived from the sale's actual payment rows
// (order_payments, via row.payment_methods), the same source the Sales Payment report
// reads, with a fallback to the sale's denormalised payment_method for legacy paid
// sales that predate per-payment rows. One method → that method; several → "Mixed"
// (all methods in the tooltip); nothing paid → "Credit" (an unpaid/credit sale).
function saleModeOfPayment(o: SalesRow): { label: string; title?: string; muted: boolean } {
  const methods = o.payment_methods && o.payment_methods.length > 0
    ? o.payment_methods
    : (o.amount_paid > 0 && o.payment_method && o.payment_method.trim() ? [o.payment_method.trim()] : [])
  if (methods.length === 0) {
    if (o.payment_status === 'unpaid' || o.amount_paid <= 0) return { label: 'Credit', muted: true }
    return { label: '—', muted: true }
  }
  if (methods.length === 1) return { label: methodLabel(methods[0]), muted: false }
  return { label: 'Mixed', title: methods.map(methodLabel).join(', '), muted: false }
}

// Shape one sales row for export. Shared by the current-page export and the
// "export all pages" provider so both files have identical columns.
function toSalesExportRow(o: SalesRow) {
  return {
    date: displayDate(o.created_at),
    invoice_no: o.order_number,
    customer: o.customers?.business_name || o.customers?.name || 'Walk-in',
    phone: o.customers?.phone ?? '',
    status: o.status,
    payment_status: computePaymentStatus(o.total, o.amount_paid, o.created_at, null, o.payment_status),
    mode_of_payment: o.status === 'cancelled' ? '' : saleModeOfPayment(o).label,
    total: o.total,
    paid: o.amount_paid,
    balance: Math.max(0, o.total - o.amount_paid - (o.non_refund_returns_total ?? 0)),
  }
}

// ─── Add Payment Modal ────────────────────────────────────────────────────────

function AddPaymentModal({
  open,
  onClose,
  order,
  tenantId,
  userId,
  canAddPayment,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  order: SalesRow | null
  tenantId: string
  userId: string | undefined
  canAddPayment: boolean
  onSuccess: () => void
}) {
  const { format } = useCurrency()
  const [paymentOption, setPaymentOption] = useState<'full' | 'custom'>('full')
  const [selectedAccountId, setSelectedAccountId] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10))
  const [note, setNote] = useState('')
  const [creditBalance, setCreditBalance] = useState(0)
  const [loadingBalance, setLoadingBalance] = useState(false)
  const [creditMessage, setCreditMessage] = useState(() => getRandomMessage(CUSTOMER_CREDIT_MESSAGES))
  const [submitting, setSubmitting] = useState(false)

  const { data: paymentAccounts = [] } = useQuery({
    queryKey: ['payment-accounts', tenantId],
    enabled: open && !!tenantId,
    queryFn: () => fetchPaymentAccounts(tenantId),
  })

  const customerId = order?.customers?.id
  const customerName = order?.customers?.business_name || order?.customers?.name || 'Walk-in Customer'

  useEffect(() => {
    if (!open) return
    setCreditMessage(getRandomMessage(CUSTOMER_CREDIT_MESSAGES))
    setPaymentOption('full')
    setSelectedAccountId('')
    setPaymentAmount('')
    setNote('')
    setPaidOn(new Date().toISOString().slice(0, 10))
    if (customerId) loadCustomerBalance()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId])

  async function loadCustomerBalance() {
    if (!customerId) return
    setLoadingBalance(true)
    const arIds = await resolveAccountsByMappingKeys(supabase, tenantId, ['accounts_receivable', 'receivable'])
    const lines = await fetchAllPaged<{ debit_amount: number | null; credit_amount: number | null }>((from, to) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any).from('journal_entry_lines').select('debit_amount, credit_amount, journal_entries!inner(is_voided)')
        .eq('tenant_id', tenantId).eq('contact_id', customerId).in('account_id', arIds)
        .not('journal_entries.is_voided', 'is', true)
        .order('id', { ascending: true }).range(from, to))
    const balance = lines.reduce(
      (b, l) => b + (l.debit_amount ?? 0) - (l.credit_amount ?? 0),
      0
    )
    setCreditBalance(balance < 0 ? Math.abs(balance) : 0)
    setLoadingBalance(false)
  }

  if (!order) return null

  const outstanding = Math.max(0, order.total - (order.amount_paid || 0))
  const creditApplied = Math.min(creditBalance, outstanding)
  const remainingAfterCredit = Math.max(0, outstanding - creditApplied)
  const fmsg = formatMessage(creditMessage, customerName, format(creditBalance))

  async function handleConfirmPayment() {
    if (!order) return
    if (!canAddPayment) { toast.error('Not authorized to add payment to sale'); return }
    const fullPayAmount = remainingAfterCredit
    const customAmount = Number(paymentAmount) || 0
    const amount = paymentOption === 'full' ? fullPayAmount : customAmount
    const excess = Math.max(0, amount - fullPayAmount)

    if (!selectedAccountId) { toast.error('Please select a payment account'); return }
    if (amount <= 0) { toast.error('Please enter a valid amount'); return }

    // Section G: overpayment confirmation
    if (excess > 0 && order.customers?.id) {
      const ok = window.confirm(
        `Amount exceeds balance by ${format(excess)}. The excess will be saved as an advance for ${customerName}. Continue?`
      )
      if (!ok) return
    }

    setSubmitting(true)
    try {
      const customerId = order.customers?.id
      if (!customerId) throw new Error('This sale has no customer; cannot record an AR receipt')

      // Get COA id for the selected payment account (the RPC debits a COA account).
      const { data: payAcct } = await db('payment_accounts')
        .select('coa_account_id')
        .eq('id', selectedAccountId)
        .maybeSingle()
      const coaId = payAcct?.coa_account_id ?? selectedAccountId

      // Cash applied to THIS invoice; any excess beyond it becomes advance, which
      // the RPC represents as the unallocated portion of the AR credit.
      const cashApplied = Math.min(amount, fullPayAmount)
      const allocations = cashApplied > 0 ? [{ order_id: order.id, amount: cashApplied }] : []

      // Single atomic call — posts the journal, the order_payments row(s) and the
      // sales_orders bump in one transaction (mig 231).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: rpcErr } = await (supabase as any).rpc('post_customer_receipt_atomic', {
        p_tenant_id: tenantId,
        p_customer_id: customerId,
        p_payment_date: paidOn,
        p_methods: [{ payment_account_id: coaId, amount, payment_method: 'bank' }],
        p_allocations: allocations,
        p_note: note || null,
        p_created_by: userId ?? null,
        p_idempotency_key: crypto.randomUUID(),
      })
      if (rpcErr) throw new Error(rpcErr.message)

      // Pre-existing, no-journal advance netting: applying the customer's EXISTING
      // credit to this invoice is a pure bookkeeping bump (their AR is already net)
      // and is out of scope for Build 3's atomicity work — preserved as-is.
      if (creditApplied > 0) {
        const newAmountPaid = Math.min((order.amount_paid || 0) + cashApplied + creditApplied, order.total)
        await db('sales_orders').update({
          amount_paid: newAmountPaid,
          payment_status: newAmountPaid >= order.total ? 'paid' : newAmountPaid > 0 ? 'partial' : 'unpaid',
          payment_due: Math.max(0, order.total - newAmountPaid),
        }).eq('id', order.id)
      }

      if (excess > 0) {
        toast.success(`${format(amount)} collected! ${format(excess)} saved as advance for ${customerName}.`)
      } else if (amount === fullPayAmount) {
        toast.success(`Collection complete! ${customerName} is fully settled. ✓`)
      } else {
        toast.success(`${format(amount)} collected. ${format(fullPayAmount - amount)} remaining outstanding.`)
      }
      onClose(); onSuccess()
    } catch (err: unknown) {
      toast.error('Payment failed: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Record Payment">
      <div className="space-y-4">
        <div className="flex justify-between items-start">
          <p className="text-sm text-gray-500 font-mono">{order.order_number}</p>
          <div className="text-right">
            <p className="text-xs text-gray-500">Outstanding</p>
            <p className="text-xl font-bold text-red-600">{format(outstanding)}</p>
          </div>
        </div>

        {!loadingBalance && creditBalance > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">{fmsg.emoji}</span>
              <div>
                <p className="font-semibold text-amber-900 text-sm">{fmsg.title}</p>
                <p className="text-amber-700 text-xs mt-0.5">{fmsg.body}</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-amber-200 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-amber-700">Outstanding</span>
                <span className="font-medium text-amber-900">{format(outstanding)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-amber-700">Credit Applied ✓</span>
                <span className="font-medium text-green-700">-{format(creditApplied)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold border-t border-amber-200 pt-1 mt-1">
                <span className="text-amber-900">Remaining</span>
                <span className={remainingAfterCredit === 0 ? 'text-green-700' : 'text-red-600'}>
                  {format(remainingAfterCredit)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <label className={cn('flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
            paymentOption === 'full' ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300')}>
            <input type="radio" name="sp_paymentOption" value="full"
              checked={paymentOption === 'full'} onChange={() => setPaymentOption('full')}
              className="mt-0.5 accent-orange-500" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">Collect in Full</p>
              <p className="text-xs text-gray-500 mt-0.5">{format(remainingAfterCredit)} will be collected now.</p>
              {paymentOption === 'full' && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Payment Account *</label>
                    <select value={selectedAccountId} onChange={e => setSelectedAccountId(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none">
                      <option value="">— Select —</option>
                      {paymentAccounts.map((acc: { id: string; name: string }) => (
                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Received On *</label>
                    <input type="date" value={paidOn} onChange={e => setPaidOn(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Note</label>
                    <input type="text" value={note} onChange={e => setNote(e.target.value)}
                      placeholder="Optional"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none" />
                  </div>
                </div>
              )}
            </div>
          </label>

          <label className={cn('flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
            paymentOption === 'custom' ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300')}>
            <input type="radio" name="sp_paymentOption" value="custom"
              checked={paymentOption === 'custom'} onChange={() => setPaymentOption('custom')}
              className="mt-0.5 accent-orange-500" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">Make Collection</p>
              <p className="text-xs text-gray-500 mt-0.5">Enter any amount. Collect less = rest stays outstanding. Collect more = excess becomes customer credit.</p>
              {paymentOption === 'custom' && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Payment Account *</label>
                    <select value={selectedAccountId} onChange={e => setSelectedAccountId(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none">
                      <option value="">— Select —</option>
                      {paymentAccounts.map((acc: { id: string; name: string }) => (
                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Amount *</label>
                    <input type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)}
                      placeholder="Enter any amount"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none" />
                    {Number(paymentAmount) > 0 && (
                      <div className="mt-1.5 space-y-0.5">
                        {Number(paymentAmount) < remainingAfterCredit && (
                          <p className="text-xs text-amber-600">{format(remainingAfterCredit - Number(paymentAmount))} will remain outstanding</p>
                        )}
                        {Number(paymentAmount) === remainingAfterCredit && (
                          <p className="text-xs text-green-600">✓ Fully settled</p>
                        )}
                        {Number(paymentAmount) > remainingAfterCredit && (
                          <p className="text-xs text-blue-600">{format(Number(paymentAmount) - remainingAfterCredit)} will become customer credit on {customerName}'s account</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Received On *</label>
                    <input type="date" value={paidOn} onChange={e => setPaidOn(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Note</label>
                    <input type="text" value={note} onChange={e => setNote(e.target.value)}
                      placeholder="Optional"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none" />
                  </div>
                </div>
              )}
            </div>
          </label>
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleConfirmPayment}
            disabled={submitting || !selectedAccountId || (paymentOption === 'custom' && Number(paymentAmount) <= 0)}
            className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {submitting ? <Spinner size="sm" /> : (
              paymentOption === 'full'
                ? `Collect ${format(remainingAfterCredit)}`
                : Number(paymentAmount) > 0 ? `Collect ${format(Number(paymentAmount))}` : 'Confirm Collection'
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ─── View Payments Modal ──────────────────────────────────────────────────────

function ViewPaymentsModal({
  open,
  onClose,
  order,
}: {
  open: boolean
  onClose: () => void
  order: SalesRow | null
}) {
  const { format } = useCurrency()

  const { data: payments = [], isLoading } = useQuery({
    queryKey: ['order-payments', order?.id, 'sale'],
    enabled: open && !!order?.id,
    queryFn: () => fetchOrderPayments(order!.id, 'sale'),
  })

  return (
    <Modal open={open} onClose={onClose} title={`Payments — ${order?.order_number ?? ''}`}>
      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : payments.length === 0 ? (
        <EmptyState title="No payments recorded" description="Payments will appear here once recorded." />
      ) : (
        <div className="space-y-2">
          {payments.map(p => (
            <div key={p.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
              <div>
                <p className="text-sm font-medium text-gray-900">{format(p.amount)}</p>
                <p className="text-xs text-gray-500">{displayDate(p.paid_on)} · {p.payment_method.replace(/_/g, ' ')}</p>
                {p.note && <p className="text-xs text-gray-400">{p.note}</p>}
              </div>
              <span className="text-xs text-gray-400">{p.accounts?.name ?? '—'}</span>
            </div>
          ))}
          <div className="pt-2 flex justify-between text-sm font-semibold">
            <span>Total Paid</span>
            <span>{format(payments.reduce((s, p) => s + p.amount, 0))}</span>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ─── Edit Shipping Modal ──────────────────────────────────────────────────────

function EditShippingModal({
  open,
  onClose,
  order,
  tenantId,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  order: SalesRow | null
  tenantId: string
  onSuccess: () => void
}) {
  const [details, setDetails] = useState('')
  const [address, setAddress] = useState('')
  const [charges, setCharges] = useState('0')
  const [status, setStatus] = useState('ordered')
  const [deliveredTo, setDeliveredTo] = useState('')

  const mutation = useMutation({
    mutationFn: async () => {
      if (!order) return
      const { error } = await db('sales_orders').update({
        shipping_details: details || null,
        shipping_address: address || null,
        shipping_charges: parseFloat(charges) || 0,
        shipping_status: status,
        delivered_to: deliveredTo || null,
      }).eq('id', order.id).eq('tenant_id', tenantId)
      if (error) throw error
    },
    onSuccess: () => { toast.success('Shipping updated'); onClose(); onSuccess() },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal open={open} onClose={onClose} title="Edit Shipping">
      <div className="space-y-4">
        <div>
          <label className="label">Shipping Details</label>
          <textarea className="input min-h-[80px]" value={details} onChange={e => setDetails(e.target.value)} />
        </div>
        <div>
          <label className="label">Shipping Address</label>
          <textarea className="input min-h-[80px]" value={address} onChange={e => setAddress(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Shipping Charges (₦)</label>
            <input type="number" className="input" value={charges} min="0" step="0.01"
              onChange={e => setCharges(e.target.value)} />
          </div>
          <div>
            <label className="label">Shipping Status</label>
            <select className="input" value={status} onChange={e => setStatus(e.target.value)}>
              {['Ordered', 'Packed', 'Shipped', 'Delivered', 'Cancelled'].map(s => (
                <option key={s} value={s.toLowerCase()}>{s}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="label">Delivered To</label>
          <input className="input" value={deliveredTo} onChange={e => setDeliveredTo(e.target.value)}
            placeholder="Recipient name" />
        </div>
        <button className="btn-primary w-full h-11" disabled={mutation.isPending}
          onClick={() => mutation.mutate()}>
          {mutation.isPending ? <Spinner size="sm" /> : 'Save Shipping'}
        </button>
      </div>
    </Modal>
  )
}

// ─── Receivables Position Widget ──────────────────────────────────────────────

function ReceivablesWidget({ tenantId }: { tenantId: string }) {
  const { format } = useCurrency()
  const { data, isLoading } = useQuery({
    queryKey: ['receivables-position', tenantId],
    enabled: !!tenantId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const balanceMap = await getAllCustomerBalances(supabase, tenantId)

      let arBalance = 0
      let advanceBalance = 0
      for (const bal of balanceMap.values()) {
        if (bal > 0) arBalance += bal
        else if (bal < 0) advanceBalance += -bal
      }

      return { arBalance: Math.max(0, arBalance), advanceBalance: Math.max(0, advanceBalance) }
    },
  })

  if (isLoading) return null
  const ar = data?.arBalance ?? 0
  const adv = data?.advanceBalance ?? 0
  const net = ar - adv

  return (
    <div className="card border border-gray-100">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="h-4 w-4 text-orange-500" />
        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Receivables Position</h3>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <a href="/reports/aged-debtors" className="group">
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Total AR (owed)</p>
          <p className="text-lg font-bold text-gray-900 group-hover:text-orange-600">{format(ar)}</p>
        </a>
        <a href="/contacts" className="group">
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Customer Credits</p>
          <p className="text-lg font-bold text-amber-600 group-hover:text-amber-700">-{format(adv)}</p>
        </a>
        <a href="/reports/aged-debtors" className="group">
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Net Receivables</p>
          <p className={`text-lg font-bold ${net >= 0 ? 'text-green-600' : 'text-red-600'} group-hover:opacity-80`}>{format(net)}</p>
        </a>
      </div>
    </div>
  )
}

// ─── Kebab menu builder ───────────────────────────────────────────────────────
// Once a sale is finalised it is immutable: never edited, never deleted —
// only returned or cancelled. The menu is built from the row's status and
// payment status, dropping items that don't apply.

type BuildArgs = {
  order: SalesRow
  isCancelled: boolean
  payStatus: string
  canAddPaymentPerm: boolean
  canCancelPerm: boolean
  canEditPerm: boolean
  canPrintInvoicePerm: boolean
  canShareInvoicePerm: boolean
  onEditShipping: () => void
  onAddPayment: () => void
  onViewPayments: () => void
  onCancelSale: () => void
}

function buildSaleActionItems({
  order, isCancelled, payStatus, canAddPaymentPerm, canCancelPerm, canEditPerm,
  canPrintInvoicePerm, canShareInvoicePerm,
  onEditShipping, onAddPayment, onViewPayments, onCancelSale,
}: BuildArgs): ActionMenuItem[] {
  const viewDetails: ActionMenuItem = {
    label: 'View Details',
    icon: <Eye className="h-3.5 w-3.5" />,
    onClick: () => window.location.assign(`/sales/${order.id}`),
  }
  if (isCancelled) return [viewDetails]

  const status = order.status
  // Opening-balance brought-forward invoices (mig 386) are a carried ledger balance
  // with no line items: they can be paid down and printed, but never edited,
  // returned or cancelled (there is no sale to reverse).
  const isOpeningBalance = order.meta?.opening_balance_bf === true
  const isDraftish = status === 'draft' || status === 'quotation' || status === 'pro_forma'
  const isFinal = status === 'final' && !isOpeningBalance
  const canReturn = (status === 'final' || status === 'partially_returned') && !isOpeningBalance
  const canAddPayment = payStatus !== 'paid' && canAddPaymentPerm

  const items: ActionMenuItem[] = [viewDetails]
  if (isDraftish && canEditPerm) {
    items.push({
      label: 'Edit Sale',
      icon: <Edit2 className="h-3.5 w-3.5" />,
      onClick: () => window.location.assign(`/sales/${order.id}/edit`),
    })
  }
  items.push(
    { label: 'Edit Shipping', icon: <Truck className="h-3.5 w-3.5" />, onClick: onEditShipping },
  )
  if (canPrintInvoicePerm) {
    items.push(
      { label: 'Print Invoice', icon: <Printer className="h-3.5 w-3.5" />, onClick: () => window.print() },
      { label: 'Packing Slip', icon: <Package className="h-3.5 w-3.5" />, onClick: () => toast('Coming soon') },
    )
  }
  if (canReturn && canShareInvoicePerm) {
    items.push({
      label: 'Invoice URL',
      icon: <ExternalLink className="h-3.5 w-3.5" />,
      onClick: async () => {
        try {
          const token = await ensureShareToken(order.id, order.share_token)
          await navigator.clipboard.writeText(publicInvoiceUrl(token))
          toast.success('Public invoice link copied')
        } catch (e) {
          toast.error((e as Error).message)
        }
      },
    })
  }
  if (canAddPayment) {
    items.push({
      label: 'Add Payment',
      icon: <CreditCard className="h-3.5 w-3.5" />,
      onClick: onAddPayment,
      divider: true,
    })
    items.push({ label: 'View Payments', icon: <DollarSign className="h-3.5 w-3.5" />, onClick: onViewPayments })
  } else {
    items.push({
      label: 'View Payments',
      icon: <DollarSign className="h-3.5 w-3.5" />,
      onClick: onViewPayments,
      divider: true,
    })
  }
  if (canReturn) {
    items.push({
      label: 'Sale Return',
      icon: <RefreshCw className="h-3.5 w-3.5" />,
      onClick: () => window.location.assign(`/sales/returns/new?from=${order.order_number}`),
    })
  }
  if (isFinal && canCancelPerm) {
    items.push({
      label: 'Cancel Sale',
      icon: <XCircle className="h-3.5 w-3.5" />,
      onClick: onCancelSale,
      variant: 'danger',
      divider: true,
    })
  }
  return items
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SalesPage() {
  const { format } = useCurrency()
  const { tenant, user } = useAuthStore()
  const { can, viewScope } = usePermissions()
  const scope = viewScope('sales')
  const ownerId = scope === 'own' ? (user?.id ?? '') : undefined
  const canAddPayment = can('sales.add_payment')
  const canCancelSale = can('sales.cancel')
  const canEditSale = can('sales.edit')
  const canPrintInvoice = can('sales.print_invoice')
  const canShareInvoice = can('sales.share_invoice')
  const tid = tenant?.id ?? ''
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [filterFrom, setFilterFrom] = useState('')
  const [filterTo, setFilterTo] = useState('')
  const [filterPayStatus, setFilterPayStatus] = useState('all')
  const [filterMethod, setFilterMethod] = useState('all')
  const [filterLocation, setFilterLocation] = useState(() => searchParams.get('location') ?? 'all')
  const [statusFilter, setStatusFilter] = useState<'active' | 'cancelled' | 'draft' | 'quotation' | 'returned' | 'all'>('active')
  const [appliedFilters, setAppliedFilters] = useState<{
    from?: string; to?: string; paymentStatus?: string; locationId?: string
  }>({})
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses', tid],
    enabled: !!tid,
    queryFn: () => fetchWarehouses(tid),
  })

  const { data: allowedLocationIds } = useUserLocationIds()
  const isLocationRestricted = !!allowedLocationIds
  const accessibleWarehouses = isLocationRestricted
    ? warehouses.filter(w => allowedLocationIds!.includes(w.id))
    : warehouses

  useEffect(() => {
    if (!isLocationRestricted) return
    if (filterLocation === 'all' || !accessibleWarehouses.some(w => w.id === filterLocation)) {
      const first = accessibleWarehouses[0]
      if (first) setFilterLocation(first.id)
    }
  }, [isLocationRestricted, filterLocation, accessibleWarehouses])

  const [payingOrder, setPayingOrder] = useState<SalesRow | null>(null)
  const [viewingPayments, setViewingPayments] = useState<SalesRow | null>(null)
  const [editingShipping, setEditingShipping] = useState<SalesRow | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const idem = useIdempotencyKey()

  // Fresh idempotency key each time the cancel confirmation opens for a sale.
  useEffect(() => {
    if (cancellingId) idem.reset()
  }, [cancellingId, idem])

  // Shared filter object for the list page, the footer total and the export —
  // so all three stay in lockstep.
  const salesFilters = useMemo(() => ({
    search: debouncedSearch || undefined,
    statusFilter,
    createdBy: ownerId,
    ...appliedFilters,
  }), [debouncedSearch, statusFilter, ownerId, appliedFilters])

  // Any filter/search/status change resets to page 1, so the pager never lands
  // the user on a now-empty page.
  useEffect(() => { setPage(1) }, [salesFilters])

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['sales-orders-enhanced', tid, salesFilters, scope, page, pageSize],
    enabled: !!tid && scope !== 'none',
    queryFn: () => fetchSalesOrdersEnhanced(tid, salesFilters, { page, pageSize }),
    staleTime: 30 * 1000,
  })
  const orders = pageData?.rows ?? []
  const totalCount = pageData?.count ?? 0

  useEffect(() => {
    const loc = appliedFilters.locationId
    if (loc) setSearchParams(p => { p.set('location', loc); return p }, { replace: true })
    else setSearchParams(p => { p.delete('location'); return p }, { replace: true })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters.locationId])

  // Footer total: server-side sum of `total` across ALL matching rows (excl.
  // cancelled), not just the current page.
  const { data: activeTotal = 0 } = useQuery({
    queryKey: ['sales-orders-active-total', tid, salesFilters, scope],
    enabled: !!tid && scope !== 'none',
    queryFn: () => fetchSalesOrdersActiveTotal(tid, salesFilters),
    staleTime: 30 * 1000,
  })

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!canCancelSale) throw new Error('Not authorized to cancel sale')
      const result = await cancelSaleWithReversal({
        tenantId: tid,
        saleId: id,
        userId: user?.id,
        reason: 'Cancelled from sales list',
        idempotencyKey: idem.get(),
      })
      logAction({ tenantId: tid, userId: user?.id ?? '', action: 'UPDATE', module: 'sales', resourceType: 'sale', resourceId: id, description: 'Sale cancelled' })
      return result
    },
    onSuccess: (result) => {
      idem.reset()
      qc.invalidateQueries({ queryKey: ['sales-orders-enhanced'] })
      qc.invalidateQueries({ queryKey: ['receivables-position'] })
      toast.success(`Sale cancelled. Reversal journal posted: ${result.entryNumber}`)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function applyFilters() {
    setAppliedFilters({
      from: filterFrom || undefined,
      to: filterTo || undefined,
      paymentStatus: filterPayStatus !== 'all' ? filterPayStatus : undefined,
      locationId: filterLocation !== 'all' ? filterLocation : undefined,
    })
  }

  function clearFilters() {
    setFilterFrom(''); setFilterTo(''); setFilterPayStatus('all')
    setFilterMethod('all'); setFilterLocation('all')
    setAppliedFilters({})
  }

  const hasFilters = !!(appliedFilters.from || appliedFilters.to || appliedFilters.paymentStatus || appliedFilters.locationId)

  const exportData = useCallback(() => (orders as SalesRow[]).map(toSalesExportRow), [orders])

  const STATUS_TABS = [
    { key: 'active', label: 'Active' },
    { key: 'all', label: 'All' },
    { key: 'draft', label: 'Draft' },
    { key: 'quotation', label: 'Quotation' },
    { key: 'returned', label: 'Returned' },
    { key: 'cancelled', label: 'Cancelled' },
  ] as const

  if (scope === 'none') return <NotAuthorizedView />

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sales"
        subtitle="Sales invoices and orders"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <ExportButton
              data={exportData()}
              columns={[
                { key: 'date', header: 'Date' },
                { key: 'invoice_no', header: 'Invoice #' },
                { key: 'customer', header: 'Customer' },
                { key: 'phone', header: 'Phone' },
                { key: 'status', header: 'Status' },
                { key: 'payment_status', header: 'Payment Status' },
                { key: 'mode_of_payment', header: 'Mode of Payment' },
                { key: 'total', header: 'Total', isCurrency: true },
                { key: 'paid', header: 'Total Paid', isCurrency: true },
                { key: 'balance', header: 'Balance Due', isCurrency: true },
              ]}
              filename="sales-orders"
              requiredPermission="sales.export"
              fetchData={async () => (await fetchSalesOrdersForExport(tid, salesFilters)).map(toSalesExportRow)}
            />
            <button
              className={cn('btn-secondary flex items-center gap-1.5 text-xs py-1.5', hasFilters && 'border-brand-400 text-brand-600')}
              onClick={() => setFiltersOpen(o => !o)}
            >
              <Filter className="h-3.5 w-3.5" />
              Filters
              {hasFilters && <span className="bg-brand-500 text-white rounded-full h-4 w-4 text-[10px] flex items-center justify-center">!</span>}
            </button>
            <Link to="/sales/new" className="btn-primary flex items-center gap-1.5 text-sm">
              <Plus className="h-4 w-4" /> New Sale
            </Link>
          </div>
        }
      />

      {/* Receivables Position Widget */}
      <ReceivablesWidget tenantId={tid} />

      {/* Status tabs */}
      <div className="flex gap-1 flex-wrap">
        {STATUS_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setStatusFilter(t.key)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              statusFilter === t.key
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <input
          className="input pl-9"
          placeholder="Search by invoice number or customer…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {search && (
          <button className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearch('')}>
            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Filters bar */}
      {filtersOpen && (
        <div className="card py-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="label">Date From</label>
              <input type="date" className="input py-1.5 text-sm w-36" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
            </div>
            <div>
              <label className="label">Date To</label>
              <input type="date" className="input py-1.5 text-sm w-36" value={filterTo} onChange={e => setFilterTo(e.target.value)} />
            </div>
            <div>
              <label className="label">Payment Status</label>
              <select className="input py-1.5 text-sm" value={filterPayStatus} onChange={e => setFilterPayStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
            <div>
              <label className="label">Payment Method</label>
              <select className="input py-1.5 text-sm" value={filterMethod} onChange={e => setFilterMethod(e.target.value)}>
                <option value="all">All</option>
                {PAYMENT_METHODS.map(m => <option key={m} value={m.toLowerCase().replace(/\s/g, '_')}>{m}</option>)}
              </select>
            </div>
            {accessibleWarehouses.length > 1 && (
              <div>
                <label className="label">Location</label>
                <select className="input py-1.5 text-sm" value={filterLocation} onChange={e => setFilterLocation(e.target.value)}>
                  {!isLocationRestricted && <option value="all">All locations</option>}
                  {accessibleWarehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
            )}
            <div className="flex gap-2">
              <button className="btn-primary py-1.5 text-sm" onClick={applyFilters}>Apply</button>
              <button className="btn-secondary py-1.5 text-sm" onClick={clearFilters}>Clear</button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : orders.length === 0 ? (
        <EmptyState
          title={debouncedSearch ? 'No sales match your search' : hasFilters ? 'No orders match your filters' : 'No sales recorded yet'}
          description={
            debouncedSearch || hasFilters
              ? 'Try adjusting your search or filters.'
              : 'Use the POS to complete your first sale, or create a manual invoice.'
          }
          icon={<FileText className="h-8 w-8" />}
          action={
            !search && !hasFilters ? (
              <Link to="/sales/new" className="btn-primary inline-flex items-center gap-1.5">
                <Plus className="h-4 w-4" /> New Sale
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {['', 'Date', 'Invoice #', 'Customer', 'Phone', 'Location', 'Status', 'Payment', 'Mode', 'Total', 'Paid', 'Balance'].map(h => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(orders as SalesRow[]).map(o => {
                  const isCancelled = o.status === 'cancelled'
                  const payStatus = computePaymentStatus(o.total, o.amount_paid, o.created_at, null, o.payment_status)
                  const dueDate = getDueDate(o.created_at, null)
                  const balance = Math.max(0, o.total - o.amount_paid - (o.non_refund_returns_total ?? 0))
                  return (
                    <tr key={o.id} className={cn('hover:bg-gray-50 transition-colors', isCancelled && 'opacity-60 bg-gray-50')}>
                      <td className="px-4 py-3">
                        <ActionMenu items={buildSaleActionItems({
                          order: o,
                          isCancelled,
                          payStatus,
                          canAddPaymentPerm: canAddPayment,
                          canCancelPerm: canCancelSale,
                          canEditPerm: canEditSale,
                          canPrintInvoicePerm: canPrintInvoice,
                          canShareInvoicePerm: canShareInvoice,
                          onEditShipping: () => setEditingShipping(o),
                          onAddPayment: () => setPayingOrder(o),
                          onViewPayments: () => setViewingPayments(o),
                          onCancelSale: () => setCancellingId(o.id),
                        })} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{displayDate(o.created_at)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{o.order_number}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{o.customers?.business_name || o.customers?.name || 'Walk-in'}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{o.customers?.phone ?? '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {o.location?.name ?? <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${STATUS_BADGE[o.status] ?? 'badge-gray'} capitalize text-[10px]`}>
                          {o.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {!isCancelled && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`badge ${PAYMENT_STATUS_BADGE[payStatus] ?? 'badge-gray'} capitalize`}>
                              {payStatus}
                            </span>
                            {o.payment_indicated && (
                              <span className="badge badge-orange flex items-center gap-1 text-[10px]" title="Customer indicated payment">
                                💰 Indicated
                              </span>
                            )}
                          </div>
                        )}
                        {dueDate && payStatus !== 'paid' && !isCancelled && (
                          <p className="text-[10px] text-gray-400 mt-0.5">Due {displayDate(dueDate.toISOString())}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isCancelled ? (
                          <span className="text-gray-300">—</span>
                        ) : (() => {
                          const mode = saleModeOfPayment(o)
                          return (
                            <span className={cn('text-xs', mode.muted ? 'text-gray-400' : 'text-gray-700 font-medium')} title={mode.title}>
                              {mode.label}
                            </span>
                          )
                        })()}
                      </td>
                      <td className={cn('px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap', isCancelled && 'line-through text-gray-400')}>
                        {format(o.total)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-sage-600 whitespace-nowrap">
                        {isCancelled ? '—' : format(o.amount_paid)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {!isCancelled && balance > 0 ? (
                          <span className="font-semibold text-red-600">{format(balance)}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400 flex justify-between">
            <span>{totalCount} sale{totalCount !== 1 ? 's' : ''}</span>
            <span>
              Total (excl. cancelled): <strong className="text-gray-700">{format(activeTotal)}</strong>
            </span>
          </div>
          <div className="px-4 pb-3">
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1) }}
            />
          </div>
        </div>
      )}

      {/* Modals */}
      <AddPaymentModal
        open={!!payingOrder && canAddPayment}
        onClose={() => setPayingOrder(null)}
        order={payingOrder}
        tenantId={tid}
        userId={user?.id}
        canAddPayment={canAddPayment}
        onSuccess={() => qc.invalidateQueries({ queryKey: ['sales-orders-enhanced'] })}
      />

      <ViewPaymentsModal
        open={!!viewingPayments}
        onClose={() => setViewingPayments(null)}
        order={viewingPayments}
      />

      <EditShippingModal
        open={!!editingShipping}
        onClose={() => setEditingShipping(null)}
        order={editingShipping}
        tenantId={tid}
        onSuccess={() => qc.invalidateQueries({ queryKey: ['sales-orders-enhanced'] })}
      />

      {/* Cancel confirm */}
      <Modal open={!!cancellingId} onClose={() => setCancellingId(null)} title="Cancel Sale">
        <div className="space-y-4">
          <div className="flex items-start gap-3 bg-red-50 rounded-xl p-4">
            <XCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">This will cancel the sale</p>
              <p className="text-xs text-red-600 mt-1">A GL reversal journal will be posted, stock will be restored, and payments will be detached — all atomically. This cannot be undone.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button className="btn-secondary flex-1" onClick={() => setCancellingId(null)}>Keep Sale</button>
            <button
              className="btn-danger flex-1"
              disabled={cancelMutation.isPending}
              onClick={() => {
                if (cancellingId) cancelMutation.mutate(cancellingId, { onSuccess: () => setCancellingId(null) })
              }}
            >
              {cancelMutation.isPending ? <Spinner size="sm" /> : 'Cancel Sale'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
